import React, { useState } from 'react';
import { Copy, Check, Calendar, Wand2 } from 'lucide-react';
import type { Customer } from './CustomerManagement';
import { resolveBevollmaechtigte, splitPhone, splitStrasse, type BevollmaechtigteProfil } from '../lib/bevollmaechtigte';
import { formatFoerderDatum } from '../lib/foerderDatum';
import type { FillField } from '../lib/bafaFormFill';
import { splitName } from '../lib/bafaFormFill';

interface Consultant extends BevollmaechtigteProfil {
  contactPerson?: string;
  advisorNumber?: string;
}

/** Aus der Vollmacht des Kunden ausgelesen (siehe `GET /customers/:id/vollmacht-bevollmaechtigte`). */
export interface VollmachtAngaben {
  quelle: 'unterschrieben' | 'erzeugt';
  stand: string;
  daten: {
    organisation?: string;
    anrede?: string;
    vorname?: string;
    nachname?: string;
    strasse?: string;
    plz?: string;
    ort?: string;
    telefon?: string;
    email?: string;
  };
}

/** Welche BAFA-Antragsseite der iframe gerade zeigt — steuert, welcher Abschnitt rechts
 *  sichtbar ist. 'antragsteller-selbst' und 'antragsteller-unternehmen' teilen sich dieselbe
 *  URL (…/personalInfo/antragsteller): nur bei direkter Selbst-Antragstellung fragt BAFA die
 *  E-Mail ein zweites Mal zur Bestätigung ab (Feld „email2") — das unterscheidet die beiden
 *  Varianten, nicht die URL. 'andere' deckt alles ab, wofür es keinen Datenvorschlag gibt
 *  (Standort, Leitstelle, sowie alles nach „Bestätigungen": Dokument-Upload/Abschluss). */
export type AntragPage =
  | 'ozg'
  | 'antragsteller-selbst'
  | 'bevollmaechtigter'
  | 'antragsteller-unternehmen'
  | 'beratungsunternehmen'
  | 'unternehmensdaten'
  | 'bestaetigungen'
  | 'andere';

interface Props {
  customer: Customer;
  consultant?: Consultant | null;
  vollmacht?: VollmachtAngaben | null;
  onFillSection?: (fields: FillField[]) => void;
  /** Nur noch der zur aktuellen BAFA-Seite passende Abschnitt wird angezeigt (Kundenwunsch:
   *  vorher stand immer der komplette Datensatz da, unabhängig von der sichtbaren Seite). */
  page?: AntragPage;
}

function CopyField({ label, value, icon }: { label: string; value: string | null | undefined; icon?: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const display = value || '—';
  const canCopy = !!value;

  const handleCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(value!);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <div className="flex items-start py-2 border-b border-slate-100 last:border-0 gap-2">
      {canCopy && (
        <button
          onClick={handleCopy}
          aria-label="Kopieren"
          className="shrink-0 mt-0.5 p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : (icon ?? <Copy className="w-3.5 h-3.5" />)}
        </button>
      )}
      {!canCopy && <div className="w-7 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className="text-sm text-slate-900 break-words">{display}</p>
      </div>
    </div>
  );
}

function SelectField({ label, value }: { label: string; value: string | null | undefined }) {
  const display = value || '—';
  return (
    <div className="flex items-start py-2 border-b border-slate-100 last:border-0 gap-2">
      <span className="shrink-0 mt-1.5 w-7 flex justify-center">
        <span className="w-3.5 h-3.5 rounded-full border-2 border-blue-400 bg-blue-100 block" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className="text-sm text-slate-900 break-words">{display}</p>
      </div>
    </div>
  );
}

/**
 * Datumsfeld — kopierbar wie alle anderen Felder und im Format des BAFA-Formulars
 * (TT.MM.JJJJ). Gespeichert wird ISO (JJJJ-MM-TT, aus <input type="date">); die
 * rohe ISO-Form ins Formular zu kopieren wird dort nicht angenommen.
 */
function DateField({ label, value }: { label: string; value: string | null | undefined }) {
  const deutsch = formatFoerderDatum(value) || undefined;
  return <CopyField label={label} value={deutsch} icon={<Calendar className="w-3.5 h-3.5" />} />;
}

function SectionTitle({ title, onFill }: { title: string; onFill?: () => void }) {
  return (
    <div className="mt-5 mb-2 first:mt-0">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 pb-1 border-b border-slate-200">{title}</h3>
      {onFill && (
        <button
          onClick={onFill}
          className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-violet-600 border border-violet-200 rounded-lg px-2 py-1 hover:bg-violet-50 transition-colors"
        >
          <Wand2 className="w-3.5 h-3.5" />
          Ausfüllen
        </button>
      )}
    </div>
  );
}

function TipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 mb-1 px-2 py-2 bg-green-50 rounded-lg border border-green-200">
      <p className="text-xs text-green-800">💡 {children}</p>
    </div>
  );
}

export function KundendatenPanel({ customer, consultant, vollmacht, onFillSection, page }: Props) {
  const { vorname: beraterVorname, nachname: beraterNachname } = splitName(consultant?.contactPerson);
  const wzCode = customer.wirtschaftszweig?.split(' – ')[0]?.trim() || customer.wirtschaftszweig;

  // Das BAFA-Formular fragt die Bevollmächtigung VOR der antragstellenden Person ab —
  // Reihenfolge hier deshalb identisch, damit man von oben nach unten durchgehen kann.
  //
  // Liegt eine auslesbare Vollmacht vor, gewinnt sie gegen das Berater-Profil: Das BAFA
  // gleicht den Antrag mit dem unterschriebenen Dokument ab, und Profiländerungen nach
  // der Unterschrift (Firmenname, E-Mail, Anschrift) würden sonst still auseinanderlaufen.
  const ausProfil = resolveBevollmaechtigte(consultant, customer);
  const v = vollmacht?.daten;
  const telefonAusVollmacht = splitPhone(v?.telefon);
  const bev = v
    ? {
        organisation: v.organisation ?? ausProfil.organisation,
        anrede: v.anrede ?? ausProfil.anrede,
        vorname: v.vorname ?? ausProfil.vorname,
        nachname: v.nachname ?? ausProfil.nachname,
        land: ausProfil.land,
        plz: v.plz ?? ausProfil.plz,
        ort: v.ort ?? ausProfil.ort,
        // Straße und Hausnummer stehen in der Vollmacht in EINEM Feld, das
        // Antragsformular fragt sie getrennt ab.
        ...splitStrasse(v.strasse) ?? { strasse: ausProfil.strasse, hausnummer: ausProfil.hausnummer },
        telefonVorwahl: telefonAusVollmacht.vorwahl ?? ausProfil.telefonVorwahl,
        telefonRufnummer: telefonAusVollmacht.rufnummer ?? ausProfil.telefonRufnummer,
        email: v.email ?? ausProfil.email,
      }
    : ausProfil;

  const bevollmaechtigungFelder: FillField[] = [
    { label: 'Name der bevollmächtigten Organisation', value: bev.organisation },
    { label: 'Anrede', value: bev.anrede },
    { label: 'Vorname', value: bev.vorname },
    { label: 'Nachname', value: bev.nachname },
    { label: 'Land', value: bev.land },
    { label: 'Postleitzahl', value: bev.plz },
    { label: 'Ort', value: bev.ort },
    { label: 'Straße', value: bev.strasse },
    { label: 'Hausnummer', value: bev.hausnummer },
    { label: 'Telefon Vorwahl', value: bev.telefonVorwahl },
    { label: 'Telefon Rufnummer', value: bev.telefonRufnummer },
  ];
  const bevollmaechtigungEmail = bev.email;
  // Seite „Angaben zur Bevollmächtigung" fragt die E-Mail zur Bestätigung ein zweites Mal ab.
  const bevollmaechtigungFelderMitWiederholung: FillField[] = [
    ...bevollmaechtigungFelder,
    { label: 'E-Mail-Adresse', value: bevollmaechtigungEmail },
    { label: 'E-Mail-Adresse wiederholen', value: bevollmaechtigungEmail },
  ];

  // BAFA kennt nur zwei Optionen ("Freiberuflich tätige Person" / "Unternehmen"), unsere
  // Rechtsform-Liste ist viel feiner (GmbH, Einzelunternehmen, GbR, …) — Texte matchen
  // hier nicht 1:1, deshalb feste Zuordnung statt Label-Matching auf den Wert selbst.
  const antragstellerTyp = /freiberuf/i.test(customer.rechtsform || '') ? 'Freiberuflich tätige Person' : 'Unternehmen';

  const antragstellerFelder: FillField[] = [
    // Match-Text weicht bewusst vom Anzeige-Label ab — das BAFA-Formular nennt diese
    // Felder anders ("Bei der antragstellenden Person handelt es sich um" statt
    // "Unternehmensform", "Name der Organisation" statt "Firmenname"), verifiziert
    // per Diagnose-Knopf.
    { label: 'Bei der antragstellenden Person handelt es sich um', value: antragstellerTyp },
    { label: 'Name der Organisation', value: customer.name },
    { label: 'Anrede', value: customer.anrede },
    { label: 'Vorname', value: customer.vorname },
    { label: 'Nachname', value: customer.nachname },
    { label: 'Land', value: customer.land || 'Deutschland' },
    { label: 'Postleitzahl', value: customer.plz },
    { label: 'Ort', value: customer.ort },
    { label: 'Straße', value: customer.address },
    { label: 'Hausnummer', value: customer.hausnummer },
    { label: 'Telefon Vorwahl', value: customer.phoneVorwahl },
    { label: 'Telefon Rufnummer', value: customer.phoneNummer },
    { label: 'E-Mail-Adresse', value: customer.email },
  ];
  // Variante „für mich selbst" (…/personalInfo/antragsteller MIT Feld „email2") fragt die
  // E-Mail zur Bestätigung ein zweites Mal ab; die Variante nach Bevollmächtigung (ohne
  // „email2") nicht — siehe AntragPage-Seitenerkennung.
  const antragstellerFelderMitWiederholung: FillField[] = [
    ...antragstellerFelder,
    { label: 'E-Mail-Adresse wiederholen', value: customer.email },
  ];

  const beratungsunternehmenFelder: FillField[] = [
    // Radio „BAFA-ID" / „Keine BAFA-ID bekannt" — muss zuerst gewählt werden, damit
    // BAFA das zugehörige Textfeld freischaltet.
    consultant?.advisorNumber
      ? { label: 'BAFA-ID', value: 'x', kind: 'radio' as const }
      : { label: 'Keine BAFA-ID bekannt', value: 'x', kind: 'radio' as const },
    { label: 'BAFA-ID', value: consultant?.advisorNumber },
    { label: 'Vorname', value: beraterVorname || undefined },
    { label: 'Nachname', value: beraterNachname || undefined },
  ];

  // "Privatrechtlich" (unser Wert) vs. „privat-rechtlich" (BAFA-Label) — nur dieser eine
  // Fall braucht die Bindestrich-Angleichung, „Öffentlich-rechtlich" passt schon so.
  const artJuristischePersonBafa = customer.artJuristischePerson === 'Privatrechtlich'
    ? 'privat-rechtlich'
    : customer.artJuristischePerson;

  const unternehmensdatenFelder: FillField[] = [
    { label: customer.personentyp || '', value: customer.personentyp, kind: 'radio' as const },
    ...(customer.artJuristischePerson
      ? [{ label: artJuristischePersonBafa || '', value: customer.artJuristischePerson, kind: 'radio' as const }]
      : []),
    { label: customer.vorsteuerabzug || '', value: customer.vorsteuerabzug, kind: 'radio' as const },
    // <input type=date> verlangt ISO (JJJJ-MM-TT) als .value — genau so, wie es
    // gespeichert ist. Das deutsche Anzeigeformat (formatFoerderDatum) wird von
    // date-Inputs beim programmatischen Setzen stillschweigend verworfen.
    { label: 'Gründungsdatum', value: customer.gruendung },
    { label: 'Angemeldeter Geschäftsgegenstand', value: customer.geschaeftsgegenstand },
    { label: 'Wirtschaftszweigklassifikation', value: wzCode },
  ];

  // "-" heißt bei uns "kein Wert erfasst" — soll im BAFA-Formular leer bleiben statt
  // wörtlich als Bindestrich ins Zahlenfeld zu landen.
  const ohneStrich = (v: string | null | undefined) => (v?.trim() === '-' ? undefined : v);

  const unternehmensgroesseFelder: FillField[] = [
    { label: customer.unternehmenstyp || '', value: customer.unternehmenstyp, kind: 'radio' as const },
    { label: 'Anzahl der Beschäftigten', value: customer.mitarbeiterzahl },
    { label: 'Jahresumsatz', value: ohneStrich(customer.jahresumsatz) },
    { label: 'Jahresbilanzsumme', value: ohneStrich(customer.jahresbilanzsumme) },
  ];

  const aktuelleSeite = page ?? 'andere';

  return (
    <div className="p-4 overflow-y-auto h-full">
      {aktuelleSeite === 'bevollmaechtigter' && (
        <>
          <SectionTitle title="Angaben zur Bevollmächtigung" onFill={onFillSection ? () => onFillSection(bevollmaechtigungFelderMitWiederholung) : undefined} />
          <CopyField label="Name der bevollmächtigten Organisation" value={bev.organisation} />
          <CopyField label="Anrede" value={bev.anrede} />
          <CopyField label="Vorname" value={bev.vorname} />
          <CopyField label="Nachname" value={bev.nachname} />
          <CopyField label="Land" value={bev.land} />
          <CopyField label="Postleitzahl" value={bev.plz} />
          <CopyField label="Ort" value={bev.ort} />
          <CopyField label="Straße" value={bev.strasse} />
          <CopyField label="Hausnummer" value={bev.hausnummer} />
          <CopyField label="Telefon Vorwahl" value={bev.telefonVorwahl} />
          <CopyField label="Telefon Rufnummer" value={bev.telefonRufnummer} />
          <CopyField label="E-Mail-Adresse" value={bevollmaechtigungEmail} />
          <CopyField label="E-Mail-Adresse wiederholen" value={bevollmaechtigungEmail} />
        </>
      )}

      {(aktuelleSeite === 'antragsteller-selbst' || aktuelleSeite === 'antragsteller-unternehmen') && (
        <>
          <SectionTitle
            title="Angaben zur antragstellenden Person"
            onFill={onFillSection
              ? () => onFillSection(aktuelleSeite === 'antragsteller-selbst' ? antragstellerFelderMitWiederholung : antragstellerFelder)
              : undefined}
          />
          <SelectField label="Unternehmensform" value={customer.rechtsform} />
          <CopyField label="Firmenname" value={customer.name} />
          <CopyField label="Anrede" value={customer.anrede} />
          <CopyField label="Vorname" value={customer.vorname} />
          <CopyField label="Nachname" value={customer.nachname} />
          <CopyField label="Land" value={customer.land || 'Deutschland'} />
          <CopyField label="Postleitzahl" value={customer.plz} />
          <CopyField label="Ort" value={customer.ort} />
          <CopyField label="Straße" value={customer.address} />
          <CopyField label="Nr." value={customer.hausnummer} />
          <CopyField label="Telefon Vorwahl" value={customer.phoneVorwahl} />
          <CopyField label="Telefon Rufnummer" value={customer.phoneNummer} />
          <CopyField label="E-Mail-Adresse" value={customer.email} />
          {aktuelleSeite === 'antragsteller-selbst' && (
            <CopyField label="E-Mail-Adresse wiederholen" value={customer.email} />
          )}
        </>
      )}

      {aktuelleSeite === 'beratungsunternehmen' && (
        <>
          <SectionTitle title="Angaben zum Beratungsunternehmen" onFill={onFillSection ? () => onFillSection(beratungsunternehmenFelder) : undefined} />
          <CopyField label="BAFA-ID" value={consultant?.advisorNumber} />
          <CopyField label="Vorname" value={beraterVorname || undefined} />
          <CopyField label="Nachname" value={beraterNachname || undefined} />
        </>
      )}

      {aktuelleSeite === 'unternehmensdaten' && (
        <>
          <SectionTitle title="Unternehmensdaten" onFill={onFillSection ? () => onFillSection(unternehmensdatenFelder) : undefined} />
          <SelectField label="Rechtsform" value={customer.personentyp} />
          {customer.artJuristischePerson && (
            <SelectField label="Art der juristischen Person" value={customer.artJuristischePerson} />
          )}
          <SelectField label="Vorsteuerabzugsberechtigt" value={customer.vorsteuerabzug} />
          <DateField label="Gründungsdatum" value={customer.gruendung} />
          <CopyField label="Angemeldeter Geschäftsgegenstand" value={customer.geschaeftsgegenstand} />
          <CopyField label="Wirtschaftszweigklassifikation" value={wzCode} />
        </>
      )}

      {aktuelleSeite === 'bestaetigungen' && (
        <>
          <SectionTitle title="Unternehmensgröße je nach Unternehmenstyp" onFill={onFillSection ? () => onFillSection(unternehmensgroesseFelder) : undefined} />
          <SelectField label="Unternehmenstyp" value={customer.unternehmenstyp} />
          <CopyField label="Anzahl der Beschäftigten" value={customer.mitarbeiterzahl} />
          <CopyField label="Jahresumsatz" value={customer.jahresumsatz} />
          <CopyField label="Jahresbilanzsumme" value={customer.jahresbilanzsumme} />
          <TipBox>Die lange Bestätigungs-Erklärung oben auf der Seite bitte selbst lesen und bestätigen.</TipBox>
        </>
      )}

      {aktuelleSeite === 'ozg' && (
        <TipBox>Hier entscheiden Sie selbst: „für mich selbst" oder „als bevollmächtigte Person", sowie die Authentifizierung. Dafür gibt es keinen Datenvorschlag.</TipBox>
      )}

      {aktuelleSeite === 'andere' && (
        <TipBox>Für diese Seite gibt es keinen Datenvorschlag — bitte manuell ausfüllen bzw. auswählen und bei Bedarf Dokumente hochladen.</TipBox>
      )}
    </div>
  );
}
